<script setup lang="ts">
import { GEO_HUB_MIN_CITY } from "#shared/utils/geoHubThresholds";
import { nomDepuisCleVille } from "#shared/utils/villeAnnuaire";

const { t } = useI18n({ useScope: "local" });

/**
 * Page programmatique SEO : boutiques solidaires par ville (2026-08-29).
 * Ex : /boutiques/lyon — « boutique association lyon », « goodies club lyon ».
 *
 * Quatrième étage géographique, calqué sur /cagnottes/[ville] : SSR, noindex
 * sous le seuil de substance, JSON-LD CollectionPage + ItemList, maillage
 * vers les hubs associations / cagnottes / événements / adhésions de la ville.
 */
const config = useRuntimeConfig();
const apiUrl = config.public.apiBaseUrl;
const assetsUrl = `${apiUrl}/api/assets`;

const route = useRoute();
const villeSlug = (route.params as Record<string, string | string[] | undefined>).ville as string;

interface ShopWithCity {
  id: string;
  title: string;
  slug: string;
  description?: string | null;
  cover_image?: string | null;
  organization_name?: string | null;
  organization_slug: string | null;
  organization_logo?: string | null;
  products?: Array<{ id: string; name: string }>;
  city_slug: string;
  city_name: string;
}

const { data, pending } = await useAsyncData(
  `shops-city-${villeSlug}`,
  async () => {
    try {
      return await $fetch<{
        cities: Array<{ slug: string; name: string; count: number }>;
        shops: ShopWithCity[];
        cityName: string | null;
        redirection?: string | null;
      }>(`/api/shops/by-city?city=${villeSlug}`);
    } catch {
      return { cities: [], shops: [], cityName: null, redirection: null };
    }
  },
  { server: true, lazy: false },
);

// Ancienne clé de ville (lot 4 : suffixe d'outre-mer ajouté, commune
// renommée par la BAN) : 301 vers la nouvelle clé, même hub.
if (data.value?.redirection) {
  await navigateTo(`/boutiques/${data.value.redirection}`, { redirectCode: 301 });
}

const shops = computed(() => data.value?.shops || []);
const cityName = computed(() => {
  if (data.value?.cityName) return data.value.cityName;
  return nomDepuisCleVille(villeSlug);
});

const canonicalUrl = `https://donoor.org/boutiques/${villeSlug}`;
const seoTitle = computed(() => t('seo.title', { ville: cityName.value }));
const seoDescription = computed(() =>
  t('seo.description', {
    count: shops.value.length,
    s: shops.value.length > 1 ? 's' : '',
    ville: cityName.value,
  }),
);

const shouldNoindex = computed(() => !pending.value && shops.value.length < GEO_HUB_MIN_CITY);
const robotsContent = useRobotsContent(shouldNoindex);

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogImage: () => `https://donoor.org/api/og/recherche?type=boutiques&count=${shops.value.length}`,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogUrl: canonicalUrl,
  ogLocale: "fr_FR",
  twitterCard: "summary_large_image",
  robots: () => robotsContent.value,
});

useHead({
  link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
  meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
  script: computed(() => [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: seoTitle.value,
        description: seoDescription.value,
        url: canonicalUrl,
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: t('breadcrumb.accueil'), item: "https://donoor.org" },
            { "@type": "ListItem", position: 2, name: t('breadcrumb.boutiques'), item: "https://donoor.org/recherche/boutiques" },
            { "@type": "ListItem", position: 3, name: cityName.value, item: canonicalUrl },
          ],
        },
        ...(shops.value.length > 0 && {
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: shops.value.length,
            itemListElement: shops.value.slice(0, 20).map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "Store",
                name: s.title,
                url: `https://donoor.org/boutique/${s.slug}`,
                ...(s.organization_name && {
                  parentOrganization: { "@type": "Organization", name: s.organization_name },
                }),
              },
            })),
          },
        }),
      }),
    },
  ]),
});

const getImageUrl = (fileId: string | null | undefined) =>
  fileId ? `${assetsUrl}/${fileId}?width=600&height=400&fit=cover&format=webp&quality=80` : null;
</script>

<template>
  <div class="min-h-screen bg-white">
    <RechercheUnifiedHero
      active-type="boutiques"
      :h1="t('hero.h1', { ville: cityName })"
      :intro="t('hero.intro', { count: shops.length, s: shops.length > 1 ? 's' : '', ville: cityName })"
      :ville-slug="villeSlug"
      :ville-name="cityName"
    />

    <div class="px-4 py-10 sm:py-12">
      <UContainer>
      <div v-if="pending" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <div v-for="i in 6" :key="i" class="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
          <div class="h-44 bg-gray-200" />
          <div class="p-4 space-y-2">
            <div class="h-4 w-3/4 bg-gray-200 rounded" />
            <div class="h-3 w-1/2 bg-gray-100 rounded" />
          </div>
        </div>
      </div>

      <div v-else-if="shops.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <NuxtLink
          v-for="s in shops"
          :key="s.id"
          :to="`/boutique/${s.slug}`"
          :aria-label="t('card.ariaLabel', { titre: s.title, association: s.organization_name || t('card.associationDefaut'), ville: cityName })"
          class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 no-underline"
        >
          <div class="relative h-44 overflow-hidden bg-gray-100">
            <img
              v-if="s.cover_image"
              :src="getImageUrl(s.cover_image)!"
              :alt="t('card.imageAlt', { titre: s.title })"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-gray-300">
              <UIcon name="i-lucide-shopping-bag" class="size-10" />
            </div>
          </div>
          <div class="p-4">
            <div v-if="s.organization_name" class="flex items-center gap-2 mb-2">
              <img
                v-if="s.organization_logo"
                :src="`${assetsUrl}/${s.organization_logo}?width=40&height=40&fit=contain&format=webp&quality=80`"
                :alt="t('card.logoAlt', { association: s.organization_name })"
                class="size-5 rounded-full object-contain bg-white border border-gray-100"
                loading="lazy"
              />
              <span class="text-xs text-gray-500 truncate">{{ s.organization_name }}</span>
            </div>
            <h2 class="font-semibold text-gray-900 text-sm sm:text-base leading-snug line-clamp-2 group-hover:text-(--color-main) transition-colors">
              {{ s.title }}
            </h2>
            <p v-if="s.products?.length" class="mt-2 text-xs text-gray-500">
              {{ t('card.produits', { count: s.products.length, s: s.products.length > 1 ? 's' : '' }) }}
            </p>
          </div>
        </NuxtLink>
      </div>

      <div v-else class="text-center py-16">
        <UIcon name="i-lucide-shopping-bag" class="size-12 text-gray-300 mx-auto mb-4" />
        <p class="text-gray-500 mb-4">{{ t('empty.message', { ville: cityName }) }}</p>
        <NuxtLink to="/recherche/boutiques" class="text-sm text-(--color-main) hover:underline">
          {{ t('empty.voirToutes') }}
        </NuxtLink>
      </div>

      <div v-if="data?.cities && data.cities.length > 1" class="mt-16 pt-12 border-t border-gray-100">
        <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{{ t('autresVilles.titre') }}</h2>
        <p class="text-gray-500 text-sm mb-6">{{ t('autresVilles.intro') }}</p>
        <div class="flex flex-wrap gap-2">
          <NuxtLink
            v-for="city in data.cities.filter((c) => c.slug !== villeSlug).slice(0, 30)"
            :key="city.slug"
            :to="`/boutiques/${city.slug}`"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 no-underline transition-colors"
          >
            <span>{{ city.name }}</span>
            <span class="text-gray-400">{{ city.count }}</span>
          </NuxtLink>
        </div>
      </div>

      <p class="mt-12 pt-6 border-t border-gray-100 text-sm text-gray-500 leading-relaxed">
        <span class="font-medium text-gray-700">{{ t('voirAussi.label', { ville: cityName }) }}</span>
        <NuxtLink :to="`/associations/ville/${villeSlug}`" class="text-(--color-main) hover:underline ml-1">{{ t('voirAussi.associations') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/cagnottes/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('voirAussi.cagnottes') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/evenements/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('voirAussi.evenements') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/adhesions/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('voirAussi.adhesions') }}</NuxtLink>
      </p>
      </UContainer>
    </div>
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/pages/boutiques/[ville].json"></i18n>
